import React from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, FileText, Mail } from "lucide-react";

export default function Privacy() {
  return (
    <div>
      <section className="wv-hero-grain bg-gradient-to-b from-[#F8F9FA] to-white py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Juridisch</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2" data-testid="privacy-title">Privacyverklaring</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Woon Verder behandelt uw persoonsgegevens zorgvuldig en volgens de Algemene Verordening Gegevensbescherming (AVG/GDPR).</p>
          <p className="mt-2 text-xs text-slate-500 font-mono-num">Laatst bijgewerkt: {new Date().toLocaleDateString("nl-NL", { year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-12 space-y-10 text-slate-700 leading-relaxed">
        <div className="p-5 rounded-2xl bg-[color:var(--wv-green)]/5 border border-[color:var(--wv-green)]/20 flex items-start gap-3">
          <Shield className="w-6 h-6 text-[color:var(--wv-green-dark)] flex-shrink-0 mt-1" />
          <p className="text-slate-700"><strong>In één zin:</strong> Woon Verder verzamelt alleen gegevens die nodig zijn om onze dienst te leveren, deelt deze niet met derden voor marketing en houdt zich strikt aan de AVG.</p>
        </div>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">1. Wie is verantwoordelijk?</h2>
          <p className="mt-3">Woon Verder is de verwerkingsverantwoordelijke voor de verwerking van uw persoonsgegevens.</p>
          <ul className="mt-3 space-y-1">
            <li>Handelsnaam: Woon Verder</li>
            <li>Eigenaar: Bryan · BK Capital Group</li>
            <li>Vestiging: Nederland</li>
            <li>Contact: <a href="mailto:info@woonverder.eu" className="text-[color:var(--wv-navy)] font-semibold hover:underline">info@woonverder.eu</a></li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">2. Welke gegevens verzamelen wij?</h2>
          <h3 className="font-semibold mt-4 text-slate-900">Van verkopers:</h3>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li>Naam, e-mailadres, telefoonnummer</li>
            <li>Woningadres, kadastrale gegevens, vraagprijs, woningkenmerken</li>
            <li>Foto's van de woning (door u geüpload of door onze fotograaf gemaakt)</li>
            <li>Communicatie met ons team (e-mails, notities, afspraakinformatie)</li>
            <li>Bemiddelingsovereenkomst en bijbehorende gegevens</li>
          </ul>
          <h3 className="font-semibold mt-4 text-slate-900">Van kopers / woningzoekers:</h3>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li>Naam, e-mailadres, telefoonnummer (optioneel)</li>
            <li>Zoekprofiel: gewenste locaties, budget, woningtype, kenmerken, kooptermijn</li>
            <li>Voorkeuren voor alerts (direct, dagelijks, wekelijks, uit)</li>
            <li>Match- en interesse-historie op ons platform</li>
          </ul>
          <h3 className="font-semibold mt-4 text-slate-900">Van iedere bezoeker:</h3>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li>Technische gegevens: IP-adres, browser, apparaat, bezochte pagina's</li>
            <li>Cookies (zie ons cookie-beleid hieronder)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">3. Waarvoor gebruiken wij uw gegevens?</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li><strong>Uitvoering van de dienst:</strong> Aanmelden en publiceren van uw woning, matching met kopers, communicatie over interesses en bezichtigingen.</li>
            <li><strong>Verplichte controle:</strong> Elke woningaanmelding wordt door ons gecontroleerd vóór publicatie.</li>
            <li><strong>Contact en support:</strong> Vragen beantwoorden, afspraken plannen, klachten behandelen.</li>
            <li><strong>Fotografie service:</strong> Fotografie boeken, factureren en resultaten leveren.</li>
            <li><strong>Alerts en notificaties:</strong> Alleen wanneer u zich heeft aangemeld voor onze zoekalerts.</li>
            <li><strong>Wettelijke verplichtingen:</strong> Zoals fiscale bewaarplicht en (in de toekomst) Wwft-verplichtingen.</li>
            <li><strong>Verbetering van het platform:</strong> Geaggregeerde en geanonimiseerde analyses.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">4. Rechtsgrond</h2>
          <p className="mt-3">Wij verwerken uw gegevens op basis van:</p>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li><strong>Uitvoering van een overeenkomst</strong> (bemiddelingsovereenkomst, gebruiksovereenkomst)</li>
            <li><strong>Toestemming</strong> (bijv. voor marketing-alerts, cookies)</li>
            <li><strong>Wettelijke verplichting</strong> (fiscaal, Wwft in de toekomst)</li>
            <li><strong>Gerechtvaardigd belang</strong> (fraudepreventie, platform-veiligheid)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">5. Met wie delen wij uw gegevens?</h2>
          <p className="mt-3">Wij verkopen uw gegevens <strong>nooit</strong> aan derden. Wij delen uw gegevens uitsluitend met:</p>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li><strong>Onze medewerkers</strong> die toegang nodig hebben voor het uitvoeren van de dienst.</li>
            <li><strong>Verwerkers</strong> die namens ons diensten leveren (hosting, e-mail, fotografie, betalingsverwerking). Deze zijn contractueel gebonden aan onze privacy-eisen.</li>
            <li><strong>De contra-partij bij een woningtransactie</strong> — alleen na uw expliciete toestemming en waar noodzakelijk (bv. bij notaris, financiering).</li>
            <li><strong>Overheden en toezichthouders</strong> — alleen wanneer wettelijk verplicht.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">6. Hoe lang bewaren wij uw gegevens?</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li>Actieve accounts: zolang uw account bestaat.</li>
            <li>Verkochte woningen en transacties: 7 jaar (fiscale bewaarplicht).</li>
            <li>Zoekprofielen: 2 jaar na laatste activiteit, tenzij u eerder verwijdert.</li>
            <li>Communicatie: 2 jaar na afronding.</li>
            <li>Marketing consent-logs: zolang de toestemming geldt + 5 jaar na intrekking (bewijslast).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">7. Uw rechten</h2>
          <p className="mt-3">U heeft onder de AVG de volgende rechten:</p>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li><strong>Inzage:</strong> welke gegevens hebben wij van u?</li>
            <li><strong>Rectificatie:</strong> onjuiste gegevens laten corrigeren.</li>
            <li><strong>Verwijdering:</strong> "recht om vergeten te worden" — waar wettelijk toegestaan.</li>
            <li><strong>Beperking:</strong> tijdelijk pauzeren van de verwerking.</li>
            <li><strong>Bezwaar:</strong> tegen verwerking op basis van gerechtvaardigd belang.</li>
            <li><strong>Overdraagbaarheid:</strong> uw gegevens in een gestructureerd formaat ontvangen.</li>
            <li><strong>Intrekken toestemming:</strong> op elk moment, zonder gevolgen voor eerdere verwerking.</li>
            <li><strong>Klacht:</strong> bij de Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl).</li>
          </ul>
          <p className="mt-4">Mail voor het uitoefenen van deze rechten: <a href="mailto:info@woonverder.eu" className="text-[color:var(--wv-navy)] font-semibold hover:underline">info@woonverder.eu</a>. Wij reageren binnen 30 dagen.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">8. Beveiliging</h2>
          <div className="mt-3 flex items-start gap-3 p-4 rounded-xl bg-[color:var(--wv-navy)]/5">
            <Lock className="w-6 h-6 text-[color:var(--wv-navy)] flex-shrink-0 mt-1" />
            <div>
              <p>Wij nemen technische en organisatorische maatregelen om uw gegevens te beveiligen:</p>
              <ul className="mt-2 list-disc ml-6 space-y-1">
                <li>Versleutelde verbindingen (HTTPS/TLS)</li>
                <li>Wachtwoorden opgeslagen met bcrypt-hashing</li>
                <li>JWT-tokens met korte levensduur, opgeslagen in httpOnly cookies</li>
                <li>Rol-gebaseerde toegangscontrole (RBAC)</li>
                <li>Rate limiting en fraude-detectie</li>
                <li>Reguliere backups en toegangslogs</li>
                <li>Foto's en documenten alleen toegankelijk voor bevoegde partijen</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">9. Cookies</h2>
          <p className="mt-3">Wij gebruiken cookies in vier categorieën:</p>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li><strong>Noodzakelijk</strong> — voor het functioneren van de site (login, taal, sessie). Altijd actief.</li>
            <li><strong>Voorkeuren</strong> — onthouden van uw taal en filterinstellingen. Optioneel.</li>
            <li><strong>Analytics</strong> — geanonimiseerd bezoek meten. Alleen met uw toestemming.</li>
            <li><strong>Marketing</strong> — momenteel niet actief. Als dit verandert vragen wij uw expliciete toestemming.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">10. Internationale doorgifte</h2>
          <p className="mt-3">Uw gegevens worden binnen de EU verwerkt. Waar wij gebruik maken van dienstverleners buiten de EU, borgen wij passende maatregelen (Standard Contractual Clauses).</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">11. Kinderen</h2>
          <p className="mt-3">Ons platform is niet bedoeld voor personen jonger dan 16 jaar. Wij verzamelen niet bewust gegevens van kinderen.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">12. Wijzigingen</h2>
          <p className="mt-3">Wij kunnen deze verklaring aanpassen wanneer wetgeving, technologie of onze dienstverlening dat vereist. Belangrijke wijzigingen kondigen we via e-mail of via het platform aan.</p>
        </section>

        <div className="p-6 rounded-2xl bg-[color:var(--wv-obsidian)] text-white flex items-start gap-4">
          <Mail className="w-6 h-6 text-[color:var(--wv-green)] flex-shrink-0 mt-1" />
          <div>
            <p className="font-display font-bold text-lg">Vragen over privacy?</p>
            <p className="text-white/80 mt-1">Mail ons via <a href="mailto:info@woonverder.eu" className="text-[color:var(--wv-green)] font-semibold underline">info@woonverder.eu</a>. Wij helpen u graag.</p>
          </div>
        </div>

        <p className="text-xs text-slate-500 pt-6 border-t border-slate-200">
          <FileText className="w-3.5 h-3.5 inline mr-1" /> Bekijk ook onze <Link to="/algemene-voorwaarden" className="text-[color:var(--wv-navy)] font-semibold hover:underline">algemene voorwaarden</Link> en <Link to="/contact" className="text-[color:var(--wv-navy)] font-semibold hover:underline">contactpagina</Link>.
        </p>
      </article>
    </div>
  );
}
